/** @file Create structure units section for Structure Form */

import UploadFileIcon from '@mui/icons-material/UploadFile';
import { Box, Link, TextField } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import * as React from 'react';

import type { Unit } from '../../../Units/types';
import { sortByIndex } from '../../util';

type UnitCardProps = {
  className: string;
  name?: string;
  onNameChange: (name: string) => void;
  isValid: boolean;
};

/**
 * Unit Card
 *
 * @param {object} props - Component properties
 * @returns {React.Component} - Unit Card
 */
const UnitCard = (props: UnitCardProps) => {
  return (
    <TextField
      className={props.className}
      label={'Unit Name'}
      name={'id'}
      required={true}
      value={props.name}
      // TODO: backend OpenAPI does not enforce this length. Add `maxLength: 64`
      // to `Unit.name` in customer-lambdas/doc/webapp.yaml (mirroring units.name
      // varchar(64) in db_setup.sql), then delete this client-side cap + counter.
      // Today the DB write fails with a cryptic string_data_right_truncation
      // error if the cap is bypassed.
      inputProps={{ maxLength: 64 }}
      // TODO: backend OpenAPI does not enforce non-empty. Add `minLength: 1` to
      // `Unit.name` in customer-lambdas/doc/webapp.yaml — DB column is NOT NULL but
      // accepts empty strings. The wizard's Next button is gated by isUnitValid in
      // Structures/util.ts; this `error`/`isValid` wiring just shows the user WHICH
      // card is the blank one.
      error={!props.isValid}
      helperText={!props.isValid ? 'Unit name is required' : `${(props.name ?? '').length}/64`}
      FormHelperTextProps={{ sx: { textAlign: 'right', m: 0 } }}
      onChange={(evt: React.ChangeEvent<HTMLInputElement>) => {
        props.onNameChange(getEventTarget(evt).value);
      }}
    />
  );
};

/**
 * Get correct event target
 *
 * @param  {Event} evt - Event
 * @returns {HTMLElement} - Resolved event target
 */
const getEventTarget = (evt) => evt.currentTarget || evt.target;

/**
 * Classname/style generator
 */
const useStyles: () => {
  root: string;
  input: string;
  sameRowInputContainer: string;
  cardInput: string;
  cardInputContainer: string;
} = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
  },
  input: {
    margin: theme.spacing(1),
  },
  sameRowInputContainer: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
  },
  cardInput: {
    margin: theme.spacing(1),
    display: 'flex',
    marginTop: '15px',
  },
  cardInputContainer: {
    padding: '15px',
  },
}));

type Props = {
  onNameChange: (id: string, name: string) => void;
  onImportUnits: () => void;
  units: Unit[];
  unitErrorObj: { [id: string]: boolean };
};

export const CreateUnits = (props: Props): JSX.Element => {
  const classes = useStyles();

  return (
    <div className={classes.root}>
      <div className={classes.cardInputContainer}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <UploadFileIcon fontSize="small" color="primary" />
          <Link component="button" type="button" variant="body2" onClick={props.onImportUnits}>
            Import units from spreadsheet
          </Link>
        </Box>
        {props.units.sort(sortByIndex).map((unit) => (
          <UnitCard
            className={classes.cardInput}
            isValid={!props.unitErrorObj[unit.id]}
            key={unit.id}
            name={unit.name}
            onNameChange={(newName) => props.onNameChange(unit.id, newName)}
          />
        ))}
      </div>
    </div>
  );
};
